import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client";
import type { PinnedRec, Recommendation } from "../api/types";
import RatingBadge from "./RatingBadge";
import ScoreRing from "./ScoreRing";

const HORIZON_LABEL: Record<Recommendation["horizon"], string> = {
  intraday: "Intraday (~1d)",
  short: "Short (≤1 wk)",
  medium: "Medium (1–3 mo)",
  long: "Long (6–12 mo)",
};

function PinButton({ recId, pinned }: { recId: number; pinned?: PinnedRec }) {
  const qc = useQueryClient();
  const isPinned = pinned !== undefined;

  const pinMut = useMutation({
    mutationFn: async () => api.post("/pinned", { rec_id: recId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pinned"] }),
  });
  const unpinMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/pinned/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pinned"] }),
  });

  const busy = pinMut.isPending || unpinMut.isPending;
  const handle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    if (isPinned && pinned) unpinMut.mutate(pinned.id);
    else pinMut.mutate();
  };

  if (isPinned) {
    // Pinned state: emerald default, swap to rose "Unpin" on hover.
    // Tooltip explains the soft-delete-restore behavior so an accidental
    // unpin isn't scary.
    return (
      <button
        onClick={handle}
        disabled={busy}
        title="Pinned to backtest journal — click to unpin (entry price is preserved if you re-pin)"
        className="group inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-medium uppercase tracking-wide transition border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:border-rose-500/40 hover:bg-rose-500/10 hover:text-rose-300 disabled:opacity-50"
      >
        <span className="inline-flex items-center gap-1 group-hover:hidden">
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3" aria-hidden="true">
            <path fillRule="evenodd" d="M16.704 5.296a1 1 0 0 1 0 1.414l-7.5 7.5a1 1 0 0 1-1.414 0l-3.5-3.5a1 1 0 1 1 1.414-1.414L8.5 12.086l6.79-6.79a1 1 0 0 1 1.414 0Z" clipRule="evenodd" />
          </svg>
          Pinned
        </span>
        <span className="hidden items-center gap-1 group-hover:inline-flex">
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3" aria-hidden="true">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
          Unpin
        </span>
      </button>
    );
  }

  return (
    <button
      onClick={handle}
      disabled={busy}
      title="Pin to backtest journal"
      className="inline-flex items-center gap-1 rounded-md border border-slate-700 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-slate-300 transition hover:border-sky-500/50 hover:text-sky-300 disabled:opacity-50"
    >
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3" aria-hidden="true">
        <path d="M10 2.75a.75.75 0 0 1 .75.75V4.5l3.04 1.52a1.75 1.75 0 0 1 .96 1.57V9.5l1.41 1.41a.75.75 0 0 1-.53 1.28h-4.88v3.31a.75.75 0 0 1-1.5 0v-3.31H4.37a.75.75 0 0 1-.53-1.28L5.25 9.5V7.59c0-.66.37-1.27.96-1.57L9.25 4.5V3.5A.75.75 0 0 1 10 2.75Z" />
      </svg>
      Pin
    </button>
  );
}

function stanceColor(stance: string): string {
  if (stance === "bullish") return "text-emerald-300";
  if (stance === "bearish") return "text-rose-300";
  return "text-slate-400";
}

export default function ScoreCard({ recs }: { recs: Record<string, Recommendation> }) {
  const horizons: Recommendation["horizon"][] = ["intraday", "short", "medium", "long"];
  const present = horizons.filter((h) => recs[h]);

  // Shared cache key with Hindsight → My pinned, so pinning here updates that
  // tab without refetching, and vice-versa.
  const pinned = useQuery<PinnedRec[]>({
    queryKey: ["pinned"],
    queryFn: async () => (await api.get<PinnedRec[]>("/pinned")).data,
    staleTime: 30_000,
  });
  const pinnedByRecId = useMemo(() => {
    const m = new Map<number, PinnedRec>();
    for (const p of pinned.data ?? []) {
      if (p.rec_id !== null) m.set(p.rec_id, p);
    }
    return m;
  }, [pinned.data]);

  if (present.length === 0) {
    return (
      <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
        <div className="mb-1 text-sm font-medium text-slate-300">Composite score</div>
        <p className="text-sm text-slate-500">
          No score yet. Scores recompute every 30 minutes once an analysis exists.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <div className="text-sm font-medium text-slate-300">Composite score</div>
        <div className="text-[11px] text-slate-500">
          Analysis × confidence + momentum + sentiment
        </div>
      </div>
      <div className={`grid gap-3 ${present.length === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
        {present.map((h) => {
          const r = recs[h];
          return (
            <div key={h} className="flex flex-col items-center rounded-xl border border-slate-800 bg-slate-950/40 p-3">
              <div className="mb-2 flex w-full items-center justify-between text-[10px] uppercase tracking-wide text-slate-400">
                <span>{HORIZON_LABEL[h]}</span>
                <span className={`font-semibold ${stanceColor(r.stance)}`}>{r.stance}</span>
              </div>
              <ScoreRing score={r.score} />
              <div className="mt-2">
                <RatingBadge score={r.score} size="md" />
              </div>
              <div className="mt-3 w-full flex justify-end">
                <PinButton recId={r.id} pinned={pinnedByRecId.get(r.id)} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
